import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

function Signup() {
  const [formData, setFormData] = useState({
    accessCode: '',
    clinicName: '',
    subdomain: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate subdomain from clinic name
    if (name === 'clinicName') {
      const subdomain = value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 30);
      setFormData(prev => ({
        ...prev,
        subdomain
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await register(formData);

    if (result.success) {
      toast.success('Welcome to PhysioPro! Your 14-day free trial has started.');
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setIsLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0A0E27 0%, #1a2344 100%)',
      padding: '2rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '3rem',
        width: '100%',
        maxWidth: '550px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ 
            fontSize: '2rem', 
            marginBottom: '0.5rem',
            color: 'var(--primary)'
          }}>
            Start Your Free Trial
          </h1>
          <p style={{ color: 'var(--text-light)' }}>
            14 days free, no credit card required
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Access Code */}
          <div style={{
            background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
            padding: '1.5rem',
            borderRadius: 'var(--radius)',
            marginBottom: '1.5rem',
            border: '2px solid #4caf50'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#2e7d32' }}>Access Code</h3>
            
            <div className="form-group">
              <label className="form-label">Enter your access code *</label>
              <input
                type="text"
                name="accessCode"
                className="form-input"
                placeholder="Enter code provided by PhysioPro"
                value={formData.accessCode}
                onChange={handleChange}
                required
                disabled={isLoading}
                style={{ textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                Contact us to request an access code for your free trial
              </p>
            </div>
          </div>

          {/* Clinic Info */}
          <div style={{
            background: 'var(--gray-50)',
            padding: '1.5rem',
            borderRadius: 'var(--radius)',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Clinic Information</h3>
            
            <div className="form-group">
              <label className="form-label">Clinic Name *</label>
              <input
                type="text"
                name="clinicName"
                className="form-input"
                placeholder="ABC Physiotherapy"
                value={formData.clinicName}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Subdomain *</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  name="subdomain"
                  className="form-input"
                  placeholder="abc"
                  value={formData.subdomain}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  pattern="[a-z0-9]{3,50}"
                  title="Only lowercase letters and numbers, 3-50 characters"
                  style={{ flex: 1 }}
                />
                <span style={{ color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
                  .physiopro.com
                </span>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                This will be your clinic's web address
              </p>
            </div>
          </div>

          {/* Personal Info */}
          <div style={{
            background: 'var(--gray-50)',
            padding: '1.5rem',
            borderRadius: 'var(--radius)',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Your Information</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  className="form-input"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  className="form-input"
                  placeholder="Smith"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                name="password"
                className="form-input"
                placeholder="Min. 6 characters"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone (optional)</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                placeholder="+44 7700 900000"
                value={formData.phone}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', fontSize: '1.1rem', padding: '1rem' }}
            disabled={isLoading}
          >
            {isLoading ? 'Creating your account...' : 'Start Free Trial'}
          </button>
        </form>

        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: 'var(--gray-50)',
          borderRadius: 'var(--radius)',
          fontSize: '0.875rem',
          color: 'var(--text-light)',
          textAlign: 'center'
        }}>
          ✓ 14-day free trial<br />
          ✓ No credit card required<br />
          ✓ Cancel anytime
        </div>

        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          fontSize: '0.875rem'
        }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600 }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Signup;
